import * as attackStages from 'components/attacks/utils/attack-stages'
import { isInfraAttackNode } from 'components/scenarios/utils/graph/nodes/typeguards'
import theme from 'theme'
import {
  type GraphNode,
  ScenarioGraphNodeType,
  ScenarioLifecycle
} from 'types/api/scenarios/scenario'

export const NODE_TYPE_COLOR = {
  [ScenarioGraphNodeType.InfraAttack]: 'blue',
  [ScenarioGraphNodeType.SynchronousStatusCheck]: 'green',
  [ScenarioGraphNodeType.ContinuousStatusCheck]: 'green'
}

export const dotTypeColors = {
  blue: theme.colors.blue[0],
  gray: theme.colors.steel[1],
  green: theme.colors.green[0],
  orange: theme.colors.orange[0],
  red: theme.colors.red[0],
  yellow: theme.colors.yellow[0]
}

const DOT_COLOR = {
  ACTIVE: dotTypeColors.blue,
  SUCCESSFUL: dotTypeColors.green,
  HALTED: dotTypeColors.yellow,
  HALT_REQUESTED: dotTypeColors.red,
  FAILED: dotTypeColors.red,
  NOT_STARTED: dotTypeColors.gray
}

export const LIFECYCLE = {
  NOT_STARTED: [ScenarioLifecycle.NotStarted],
  ACTIVE: [ScenarioLifecycle.Active],
  SUCCESSFUL: [ScenarioLifecycle.Successful],
  HALTED: [ScenarioLifecycle.Halted],
  HALT_REQUESTED: [ScenarioLifecycle.HaltRequested],
  FAILED: [ScenarioLifecycle.Failed]
}

export const FINISHED_LIFECYCLE = [
  ...LIFECYCLE.SUCCESSFUL,
  ...LIFECYCLE.FAILED,
  ...LIFECYCLE.HALTED,
  ...LIFECYCLE.HALT_REQUESTED
]

export const findLifecycleType = (lifecycle: ScenarioLifecycle) =>
  Object.keys(DOT_COLOR).find((key) =>
    LIFECYCLE[key].some((colorState) => colorState === lifecycle)
  )

export function getColorByState(step: GraphNode) {
  const state = Object.entries(getStateOfNodeByType(step)).find(
    ([key, value]) =>
      value === true && ['isCompleted', 'isNotStarted'].indexOf(key) === -1
  )

  if (!state?.[0]) {
    return
  }

  const lifecycle = Object.entries(LIFECYCLE).find(([, value]) => {
    const firstLifecycle = value[0]
    return firstLifecycle && state[0].replace('is', '') === firstLifecycle
  })

  if (!lifecycle?.[0]) {
    return
  }

  return DOT_COLOR[lifecycle[0]]
}

export function getStateOfNodeByType(node: GraphNode) {
  // InfraAttacks have specialized stages so if we have one, use those utilities, otherwise use graph lifecycle utils
  const stage = isInfraAttackNode(node)
    ? node.state?.task_summary?.stage
    : undefined

  if (stage) {
    return {
      isNotStarted: attackStages.isInNotStartedStage(stage),
      isActive: attackStages.isInActiveStage(stage),
      isCompleted: attackStages.isInCompleteStage(stage),
      isHalted: attackStages.isInHaltingStage(stage),
      isFailed: attackStages.isInFailedStage(stage),
      isSuccessful: attackStages.isInSuccessfulStage(stage)
    }
  }

  const lifecycle = node.state?.lifecycle ?? ScenarioLifecycle.NotStarted
  return {
    isNotStarted: isNotStarted(lifecycle),
    isActive: isActive(lifecycle),
    isCompleted: isComplete(lifecycle),
    isHalted: isHalted(lifecycle),
    isHaltRequested: isHaltRequested(lifecycle),
    isFailed: isFailed(lifecycle),
    isSuccessful: isSuccessful(lifecycle)
  }
}

// Stage
export function getLifecycleColor(lifecycle: ScenarioLifecycle) {
  const lifecycleType = findLifecycleType(lifecycle)
  return lifecycleType ? DOT_COLOR[lifecycleType] : undefined
}
export function getTypeColor(type) {
  return NODE_TYPE_COLOR[type] ?? theme.colors.steel[1]
}
export function getColor(lifecycle: ScenarioLifecycle) {
  return getLifecycleColor(lifecycle) ?? getTypeColor(lifecycle)
}
export function isComplete(lifecycle: ScenarioLifecycle) {
  return FINISHED_LIFECYCLE.includes(lifecycle)
}
export function isActive(lifecycle: ScenarioLifecycle) {
  return LIFECYCLE.ACTIVE.includes(lifecycle)
}
export function isNotStarted(lifecycle: ScenarioLifecycle) {
  return LIFECYCLE.NOT_STARTED.includes(lifecycle)
}
export const isHalted = (lifecycle: ScenarioLifecycle) =>
  LIFECYCLE.HALTED.includes(lifecycle) ||
  LIFECYCLE.HALT_REQUESTED.includes(lifecycle)
export function isHaltRequested(lifecycle: ScenarioLifecycle) {
  return LIFECYCLE.HALT_REQUESTED.includes(lifecycle)
}
export function isFailed(lifecycle: ScenarioLifecycle) {
  return LIFECYCLE.FAILED.includes(lifecycle)
}
export function isSuccessful(lifecycle: ScenarioLifecycle) {
  return LIFECYCLE.SUCCESSFUL.includes(lifecycle)
}
